// kernel/apiFetch.ts
//
// One retry when a session refresh eats an API call.
//
// When the Clerk session token is refreshed mid-flight, the middleware answers
// an API POST with a redirect to /sign-in. `fetch` follows redirects, so the
// caller does not get a 401 it can recognise - it gets HTTP 200 with an HTML
// login page, parses it as JSON, finds no `success`, and throws "Mutation
// failed." The request never reached the route, so nothing ran and nothing was
// billed; it simply vanished.
//
// Measured on a live review, 2 August 2026: assist turns were firing every ~4
// seconds and at 14:21:08 exactly three concurrent requests - /api/ai/assist,
// /api/inventory/mutate and /api/slides/ink-map - were all bounced in the same
// second. The review chain died there, mid page-sweep, with 10 of 14 pages
// labelled, no annotations and no verdicts. The server was healthy throughout
// (no restart, 200s either side).
//
// A review runs dozens of turns over several minutes, so it will always
// eventually overlap a token refresh. One overlap must not end it.

/** True when the response is really the login page wearing a 200. */
const wasBouncedToSignIn = (res: Response): boolean =>
  res.redirected && res.url.includes('/sign-in');

/**
 * fetch, with a single retry when the call was bounced to sign-in. Safe to
 * retry: a bounced request never reached the route, so it neither ran nor
 * billed. Anything else - a real 4xx/5xx, an abort - is returned untouched for
 * the caller to handle as before.
 */
export async function fetchApi(input: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init);
  if (!wasBouncedToSignIn(res)) return res;
  if (init?.signal?.aborted) return res;

  // Give the refresh a moment to land before the single retry.
  await new Promise((resolve) => setTimeout(resolve, 1200));
  if (init?.signal?.aborted) return res;

  console.warn(`[apiFetch] ${input} was bounced to sign-in (session refresh); retrying once.`);
  return fetch(input, init);
}
